import { ResponseArray, ResponseObject } from '@/types/shared'
import datacentral from '@/utils/axios/datacentral'
import qs from 'query-string'
import {
    BulkCreatePayload,
    CompanyRevenue,
    DashboardResponse,
    EmployeePerformance,
    MonthlyAllocatedRevenue,
    ProductGroupPerformance,
    SalesPerformanceResponse,
    SalesTarget,
    SalesTargetCreate,
    SalesTargetFilters,
    SalesTargetsResponse,
    SalesTargetUpdate,
    SingleTargetResponse,
    TargetVsActual,
    TopPerformingEmployee,
} from './types'

const BASE_URL = '/employees/sales-targets'

// CRUD Operations
export const getSalesTargets = async (filters: SalesTargetFilters) => {
    const response = await datacentral.get<ResponseArray<SalesTarget>>(
        `${BASE_URL}?${qs.stringify(filters, { arrayFormat: 'comma' })}`
    )
    return response.data
}

export const getSalesTarget = async (
    targetId: number
): Promise<SingleTargetResponse> => {
    const response = await datacentral.get(`${BASE_URL}/${targetId}`)
    return response.data
}

export const createSalesTarget = async (
    payload: SalesTargetCreate
): Promise<SingleTargetResponse> => {
    const response = await datacentral.post(BASE_URL, payload)
    return response.data
}

export const bulkCreateSalesTargets = async (
    payload: BulkCreatePayload
): Promise<SalesTargetsResponse> => {
    const response = await datacentral.post(`${BASE_URL}/bulk`, payload)
    return response.data
}

export const updateSalesTarget = async (
    targetId: number,
    payload: SalesTargetUpdate
): Promise<SingleTargetResponse> => {
    const response = await datacentral.put(`${BASE_URL}/${targetId}`, payload)
    return response.data
}

export const deleteSalesTarget = async (
    targetId: number
): Promise<SingleTargetResponse> => {
    const response = await datacentral.delete(`${BASE_URL}/${targetId}`)
    return response.data
}

// Performance & Analytics
export const getSalesPerformance = async (filters: {
    employee_id: number[]
    gte?: string
    lte?: string
    product_group_name?: string
    is_active?: boolean
}): Promise<SalesPerformanceResponse> => {
    const response = await datacentral.get(
        `${BASE_URL}/performance?${qs.stringify(filters, { arrayFormat: 'comma' })}`
    )
    return response.data
}

export const getDashboardStatistics = async (filters: {
    gte?: string
    lte?: string
    employee_id?: number[]
    product_group_name?: string
}): Promise<DashboardResponse> => {
    const response = await datacentral.get(
        `${BASE_URL}/performance/dashboard/employees?${qs.stringify(filters, {
            arrayFormat: 'comma',
        })}`
    )
    return response.data
}

export const getProductGroupPerformance = async (filters: {
    employee_id: number
    gte?: string
    lte?: string
}): Promise<{ message: string; results: ProductGroupPerformance[] }> => {
    const response = await datacentral.get(
        `${BASE_URL}/performance/by-product-group?${qs.stringify(filters)}`
    )
    return response.data
}

//
export const getEmployeePerformances = async (filters: {
    gte?: string
    lte?: string
    employee_id?: number[]
}) => {
    const response = await datacentral.get<ResponseObject<EmployeePerformance>>(
        `${BASE_URL}/performance/dashboard/employees?${qs.stringify(filters)}`
    )
    return response.data.result
}

// Export employee performances to Excel
export const exportEmployeePerformances = async (filters: {
    gte?: string
    lte?: string
    employee_id?: number[]
}) => {
    const response = await datacentral.get(
        `${BASE_URL}/performance/dashboard/employees?${qs.stringify({
            ...filters,
            export: 'excel',
        })}`,
        { responseType: 'blob' }
    )
    return response.data
}

export const getTopPerformingEmployees = async (filters: {
    gte?: string
    lte?: string
    limit?: number
}) => {
    const response = await datacentral.get<
        ResponseArray<TopPerformingEmployee>
    >(`${BASE_URL}/performance/dashboard/employees?${qs.stringify(filters)}`)
    return response.data.results
}

// Employee-specific endpoints
export const getEmployeeMonthlyTargets = async (
    employeeId: number,
    year: number
): Promise<SalesTargetsResponse> => {
    const response = await datacentral.get(
        `${BASE_URL}/employee/${employeeId}/monthly?year=${year}`
    )
    return response.data
}

export const getEmployeeQuarterlyTargets = async (
    employeeId: number,
    year: number
): Promise<SalesTargetsResponse> => {
    const response = await datacentral.get(
        `${BASE_URL}/employee/${employeeId}/quarterly?year=${year}`
    )
    return response.data
}

// company-revenue
export const getCompanyRevenue = async (filters: {
    gte?: string
    lte?: string
    employee_id?: number[]
}) => {
    const response = await datacentral.get<ResponseObject<CompanyRevenue>>(
        `${BASE_URL}/performance/company-revenue?${qs.stringify(filters)}`
    )
    return response.data.result
}

export const getTargetVsActual = async (filters: {
    gte?: string
    lte?: string
    employee_id?: number[]
}) => {
    const response = await datacentral.get<ResponseObject<TargetVsActual>>(
        `${BASE_URL}/performance/target-vs-actual?${qs.stringify(filters)}`
    )
    return response.data.result
}
// monthly-allocated-revenue
export const getMonthlyAllocatedRevenue = async (filters: {
    gte?: string
    lte?: string
    employee_id?: number[]
}) => {
    const response = await datacentral.get<
        ResponseArray<MonthlyAllocatedRevenue>
    >(
        `${BASE_URL}/performance/monthly-allocated-revenue?${qs.stringify(filters)}`
    )
    return response.data.results
}

// type of service-breakdown
export type ServiceBreakdown = {
    product_group_name: string
    total_revenue: number
    renewal_revenue: number
    new_sale_revenue: number
    upsell_revenue: number
    trial_revenue: number
    cancel_revenue: number
    allocation_count: number
    customer_count: number
    revenue_share: number
    renewal_growth_rate: number
    new_sale_growth_rate: number
    upsell_growth_rate: number
    avg_revenue_per_customer: number
    avg_revenue_per_allocation: number
}
//service-breakdown
export const getServiceBreakdown = async (filters: {
    gte?: string
    lte?: string
    employee_id?: number[]
}) => {
    const response = await datacentral.get<ResponseArray<ServiceBreakdown>>(
        `${BASE_URL}/performance/service-breakdown?${qs.stringify(filters)}`
    )
    return response.data.results
}
type ServiceBreakdownMonthly = {
    revenue_month: string
    total_revenue: number
    renewal_revenue: number
    new_sale_revenue: number
    upsell_revenue: number
    trial_revenue: number
    cancel_revenue: number
    allocation_count: number
    customer_count: number
}

type MonthlyServiceBreakdown = {
    product_group_name: string
    total_revenue: number
    renewal_revenue: number
    new_sale_revenue: number
    upsell_revenue: number
    trial_revenue: number
    cancel_revenue: number
    allocation_count: number
    customer_count: number
    monthly_breakdown: ServiceBreakdownMonthly[]
}
//monthly-service-breakdown
export const getMonthlyServiceBreakdown = async (filters: {
    gte?: string
    lte?: string
    employee_id?: number[]
}) => {
    const response = await datacentral.get<
        ResponseArray<MonthlyServiceBreakdown>
    >(
        `${BASE_URL}/performance/monthly-service-breakdown?${qs.stringify(filters)}`
    )
    return response.data.results
}

// ============================================
// Revenue Cash Flow (based on payment_date)
// ============================================

import { RevenueCashFlowItem, RevenueCashFlowFilters } from './types'

const BI_BASE_URL = '/reports/bi'

// Get monthly cash flow revenue grouped by payment_date
export const getRevenueCashFlow = async (filters: RevenueCashFlowFilters) => {
    const params: Record<string, string | string[]> = {}

    if (filters.gte) params.gte = filters.gte
    if (filters.lte) params.lte = filters.lte
    if (filters.employee_ids && filters.employee_ids.length > 0) {
        params['employee_ids'] = filters.employee_ids.map(String)
    }

    const response = await datacentral.get<ResponseArray<RevenueCashFlowItem>>(
        `${BI_BASE_URL}/revenue-cash-flow?${qs.stringify(params, {
            arrayFormat: 'bracket',
        })}`
    )
    return response.data.results
}
